import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { RichTextEditor } from "./RichTextEditor";
import { EmailComponent } from "./EmailBuilderV2";
import { Upload, Palette } from "lucide-react";

interface ComponentEditorProps {
  component: EmailComponent;
  onUpdate: (updates: Partial<EmailComponent>) => void;
  onClose: () => void;
}

export const ComponentEditor = ({ component, onUpdate, onClose }: ComponentEditorProps) => {
  const updateContent = (field: string, value: any) => {
    onUpdate({
      content: { ...component.content, [field]: value }
    });
  };

  const updateStyle = (field: string, value: any) => {
    onUpdate({
      style: { ...component.style, [field]: value }
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log("Image upload started:", file.name, file.type);
    
    const imageUrl = URL.createObjectURL(file);
    console.log("Created blob URL:", imageUrl);
    
    updateContent("url", imageUrl);
    updateContent("alt", file.name);
    
    console.log("Updated component content with image");
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit {component.type.charAt(0).toUpperCase() + component.type.slice(1)} Component</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Content Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium flex items-center gap-2">
              Content Settings
            </h3>
            
            {component.type === "header" && (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="header-text">Header Text</Label>
                  <Input
                    id="header-text"
                    value={component.content.text || ""}
                    onChange={(e) => updateContent("text", e.target.value)}
                    placeholder="Enter header text"
                  />
                </div>
                <div>
                  <Label htmlFor="logo-url">Logo URL (optional)</Label>
                  <Input
                    id="logo-url"
                    value={component.content.logoUrl || ""}
                    onChange={(e) => updateContent("logoUrl", e.target.value)}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>
            )}

            {component.type === "text" && (
              <div>
                <Label>Text Content</Label>
                <RichTextEditor
                  content={component.content.text || ""}
                  onChange={(content) => updateContent("text", content)}
                  placeholder="Enter your text content..."
                />
              </div>
            )}

            {component.type === "image" && (
              <div className="space-y-3">
                <div>
                  <Label>Upload Image</Label>
                  <div className="flex gap-2 mt-1">
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('image-upload')?.click()}
                      className="flex-1"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Choose File
                    </Button>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="image-url">Or Image URL</Label>
                  <Input
                    id="image-url"
                    value={component.content.url || ""}
                    onChange={(e) => {
                      console.log("Image URL changed:", e.target.value);
                      updateContent("url", e.target.value);
                    }}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <Label htmlFor="image-alt">Alt Text</Label>
                  <Input
                    id="image-alt"
                    value={component.content.alt || ""}
                    onChange={(e) => updateContent("alt", e.target.value)}
                    placeholder="Describe the image"
                  />
                </div>
                <div>
                  <Label htmlFor="image-caption">Caption (optional)</Label>
                  <Input
                    id="image-caption"
                    value={component.content.caption || ""}
                    onChange={(e) => updateContent("caption", e.target.value)}
                    placeholder="Image caption"
                  />
                </div>
              </div>
            )}

            {component.type === "button" && (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="button-text">Button Text</Label>
                  <Input
                    id="button-text"
                    value={component.content.text || ""}
                    onChange={(e) => updateContent("text", e.target.value)}
                    placeholder="Click Me"
                  />
                </div>
                <div>
                  <Label htmlFor="button-url">Link URL</Label>
                  <Input
                    id="button-url"
                    value={component.content.url || ""}
                    onChange={(e) => updateContent("url", e.target.value)}
                    placeholder="https://example.com"
                  />
                </div>
              </div>
            )}

            {component.type === "footer" && (
              <div>
                <Label>Footer Content</Label>
                <RichTextEditor
                  content={component.content.text || ""}
                  onChange={(content) => updateContent("text", content)}
                  placeholder="Enter footer content..."
                />
              </div>
            )}
          </div>

          <Separator />

          {/* Style Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Style Settings
            </h3>
            
            {component.type !== "divider" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="padding">Padding</Label>
                  <Input
                    id="padding"
                    value={component.style.padding || ""}
                    onChange={(e) => updateStyle("padding", e.target.value)}
                    placeholder="16px"
                  />
                </div>
                
                <div>
                  <Label htmlFor="text-align">Text Align</Label>
                  <Select 
                    value={component.style.textAlign || "left"} 
                    onValueChange={(value) => updateStyle("textAlign", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="bg-color">Background Color</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={component.style.backgroundColor?.includes('#') ? component.style.backgroundColor : "#3b82f6"}
                      onChange={(e) => updateStyle("backgroundColor", e.target.value)}
                      className="w-12 h-9 rounded border cursor-pointer"
                    />
                    <Input
                      id="bg-color"
                      value={component.style.backgroundColor || ""}
                      onChange={(e) => updateStyle("backgroundColor", e.target.value)}
                      placeholder="transparent"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="text-color">Text Color</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={component.style.color?.includes('#') ? component.style.color : "#000000"}
                      onChange={(e) => updateStyle("color", e.target.value)}
                      className="w-12 h-9 rounded border cursor-pointer"
                    />
                    <Input
                      id="text-color"
                      value={component.style.color || ""}
                      onChange={(e) => updateStyle("color", e.target.value)}
                      placeholder="#000000"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            )}

            {component.type === "text" && (
              <div>
                <Label htmlFor="font-size">Font Size</Label>
                <Input
                  id="font-size"
                  value={component.style.fontSize || ""}
                  onChange={(e) => updateStyle("fontSize", e.target.value)}
                  placeholder="16px"
                />
              </div>
            )}

            {component.type === "image" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Width</Label>
                    <Select 
                      value={component.style.imageWidth || "auto"} 
                      onValueChange={(value) => updateStyle("imageWidth", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto</SelectItem>
                        <SelectItem value="200px">Small (200px)</SelectItem>
                        <SelectItem value="300px">Medium (300px)</SelectItem>
                        <SelectItem value="400px">Large (400px)</SelectItem>
                        <SelectItem value="100%">Full Width</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Height</Label>
                    <Select 
                      value={component.style.imageHeight || "auto"} 
                      onValueChange={(value) => updateStyle("imageHeight", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto</SelectItem>
                        <SelectItem value="150px">150px</SelectItem>
                        <SelectItem value="200px">200px</SelectItem>
                        <SelectItem value="250px">250px</SelectItem>
                        <SelectItem value="300px">300px</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Image Alignment</Label>
                  <Select 
                    value={component.style.imageFloat || "none"} 
                    onValueChange={(value) => updateStyle("imageFloat", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Center (no text wrap)</SelectItem>
                      <SelectItem value="left">Left (text wraps right)</SelectItem>
                      <SelectItem value="right">Right (text wraps left)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {component.type === "button" && (
              <div>
                <Label htmlFor="border-radius">Border Radius</Label>
                <Input
                  id="border-radius"
                  value={component.style.borderRadius || ""}
                  onChange={(e) => updateStyle("borderRadius", e.target.value)}
                  placeholder="6px"
                />
              </div>
            )}

            {component.type === "divider" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="divider-height">Height</Label>
                  <Input
                    id="divider-height"
                    value={component.style.height || ""}
                    onChange={(e) => updateStyle("height", e.target.value)}
                    placeholder="1px"
                  />
                </div>
                <div>
                  <Label htmlFor="divider-color">Color</Label>
                  <Input
                    id="divider-color"
                    value={component.style.backgroundColor || ""}
                    onChange={(e) => updateStyle("backgroundColor", e.target.value)}
                    placeholder="hsl(var(--border))"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};